// src/app/modules/masters/bank/services/bank.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { Bank } from './models/bank.model';

export interface BankDetail {
  id: string;
  name: string;
  branch: string;
  ifsc: string;
  accountNumber: string;
  isWallet: boolean;
  isPos: boolean;
  currentBalance: number;
  todayDebit: number;
  todayCredit: number;
  netToday: number;
  status: string;
}

export interface BankSummary {
  totalBanks: number;
  totalBalance: number;
  totalTodayDebit: number;
  totalTodayCredit: number;
  totalNetToday: number;
}

@Injectable({
  providedIn: 'root',
})
export class BankService {
  private readonly baseUrl = `${environment.apiUrl}/banks`;
  private readonly financialTransactionsUrl = `${environment.apiUrl}/financial-transactions`;

  constructor(private http: HttpClient) {}

  /** ✅ Get all banks with pagination (store-based) */
  getAll(
    page: number,
    pageSize: number,
    sortBy: 'createdAt' | 'name' | 'accountType' | 'ifsc' | 'accountNumber' | 'currentBalance' | 'status' = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    status?: 'all' | 'active' | 'inactive',
    storeId?: string | null,
  ): Observable<{ data: Bank[]; total: number; sortBy: string; sortOrder: 'asc' | 'desc' }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', pageSize.toString())
      .set('sortBy', sortBy)
      .set('sortOrder', sortOrder);

    if (status && status !== 'all') {
      const statusValue = status === 'active' ? 'ACTIVE' : 'INACTIVE';
      params = params.set('status', statusValue);
    }
    if (storeId) {
      params = params.set('storeId', storeId);
    }

    return this.http.get<{ data: Bank[]; total: number; sortBy: string; sortOrder: 'asc' | 'desc' }>(
      this.baseUrl,
      { params },
    );
  }

  /** ✅ Get a bank by ID */
  getById(id: string): Observable<Bank> {
    return this.http.get<Bank>(`${this.baseUrl}/${id}`);
  }

  /** ✅ Create a new bank */
  create(bank: Partial<Bank>): Observable<Bank> {
    return this.http.post<Bank>(this.baseUrl, bank);
  }

  /** ✅ Update a bank */
  update(id: string, bank: Partial<Bank>): Observable<Bank> {
    return this.http.patch<Bank>(`${this.baseUrl}/${id}`, bank);
  }

  /** Check whether the bank is used in financial transactions (e.g. payment/receipt). */
  isUsedInTransactions(id: string): Observable<{ usedInTransactions: boolean }> {
    return this.http.get<{ usedInTransactions: boolean }>(`${this.baseUrl}/${id}/used-in-transactions`);
  }

  /** Activate a bank */
  activate(id: string): Observable<Bank> {
    return this.http.patch<Bank>(`${this.baseUrl}/activate/${id}`, {});
  }

  /** Deactivate a bank */
  deactivate(id: string): Observable<Bank> {
    return this.http.patch<Bank>(`${this.baseUrl}/deactivate/${id}`, {});
  }

  /** ✅ Delete a bank */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /** Check duplicate account number (tenant-wide; matches isWallet/isPos). */
  checkAccountNumberDuplicate(
    accountNumber: string,
    isWallet: boolean,
    isPos: boolean,
    excludeId?: string,
  ): Observable<{ exists: boolean }> {
    let params = new HttpParams()
      .set('accountNumber', accountNumber.trim())
      .set('isWallet', String(isWallet))
      .set('isPos', String(isPos));
    if (excludeId) {
      params = params.set('excludeId', excludeId);
    }
    return this.http.get<{ exists: boolean }>(`${this.baseUrl}/check-duplicate-account`, {
      params,
    });
  }

  /** Optional: Check duplicate IFSC */
  checkIfscDuplicate(ifsc: string, excludeId?: string): Observable<{ exists: boolean }> {
    const params = new HttpParams().set('ifsc', ifsc).set('excludeId', excludeId || '');

    return this.http.get<{ exists: boolean }>(`${this.baseUrl}/check-duplicate-ifsc`, {
      params,
    });
  }

  /** Optional: Search banks with pagination */
  search(query: string, skip = 0, take = 10): Observable<{ data: Bank[]; total: number }> {
    const params = new HttpParams()
      .set('query', query)
      .set('skip', skip.toString())
      .set('take', take.toString());

    return this.http.get<{ data: Bank[]; total: number }>(`${this.baseUrl}/search`, {
      params,
    });
  }

  /** Search banks (optional storeId for header-selected store). */
  searchbank(skip?: number, take?: number, search: string = '', storeId?: string | null) {
    let url = `${this.baseUrl}/search?skip=${skip ?? 0}&take=${take ?? 10}&search=${encodeURIComponent(search)}`;
    if (storeId) {
      url += `&storeId=${encodeURIComponent(storeId)}`;
    }
    return this.http.get<{ data: Bank[]; totalCount: number }>(url);
  }

  /** Get all bank balances */
  getBankBalances(): Observable<any[]> {
    return this.http.get<any[]>(`${this.financialTransactionsUrl}/bank-balances`);
  }

  /** Get specific bank balance */
  getBankBalance(bankId: string): Observable<any> {
    return this.http.get<any>(`${this.financialTransactionsUrl}/bank-balances/${bankId}`);
  }

  /** Get all bank details with today's transactions (optional storeId for header-selected store) */
  getBankDetails(status?: 'ACTIVE' | 'INACTIVE' | 'all', storeId?: string | null): Observable<BankDetail[]> {
    let params: { status?: string; storeId?: string } = status ? { status } : {};
    if (storeId) params = { ...params, storeId };
    return this.http.get<BankDetail[]>(`${this.financialTransactionsUrl}/bank-details`, { params });
  }

  /** Get specific bank detail with today's transactions */
  getBankDetail(bankId: string): Observable<BankDetail> {
    return this.http.get<BankDetail>(`${this.financialTransactionsUrl}/bank-details/${bankId}`);
  }

  /** Get bank summary (optional storeId for header-selected store) */
  getBankSummary(status?: 'ACTIVE' | 'INACTIVE' | 'all', storeId?: string | null): Observable<BankSummary> {
    let params: { status?: string; storeId?: string } = status ? { status } : {};
    if (storeId) params = { ...params, storeId };
    return this.http.get<BankSummary>(`${this.financialTransactionsUrl}/bank-summary`, { params });
  }

  /** Test bank balance update */
  testBankBalanceUpdate(bankId: string, amount: number, dc: 'DEBIT' | 'CREDIT'): Observable<any> {
    return this.http.post<any>(`${this.financialTransactionsUrl}/test-bank-balance/${bankId}`, {
      amount,
      dc,
    });
  }
}
